import * as pluralize from 'pluralize'
import { jsonToGraphQLQuery } from 'json-to-graphql-query'
import { graph } from './index'

interface Output {
  [key: string]: boolean | Output
}

/**
 * Make first letter to lowercase
 * - Function remove also space
 * @example
 * - User to user
 * - ContactCustomer to contactCustomer
 */
export const uncapitalizeFirstLetter = (str: string) => {
  // Replace string
  str = str.replace(/ /g, '')
  return str.charAt(0).toLowerCase() + str.slice(1)
}

export const capitalizeFirstLetter = (str: string) => {
  // Replace string
  str = str.replace(/ /g, '')
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export interface MultipleReadArgs {
  target: string
  alias?: string
  id?: string
  type: "count" | "findAll" | "findOne"
  paranoid? : boolean
  offset?: number
  limit?: number
  output?: any
  order?: any
  where?: any
  values?: any
  search?: any
}

export const multipleQueries = async (listQueries: MultipleReadArgs[]) => {
  const queryGraphQL = {}
  for (const query of listQueries) {
    let q: any = {}
    if (query.type === "count") {
      q = await count(query, true)
    }
    if (query.type === "findAll") {
      q = await findAll(query, true)
    }
    if (query.type === "findOne") {
      q = await findOne(query, true)
    }

    for (const key in q) {
      if (Object.prototype.hasOwnProperty.call(q, key)) {
        const element = q[key];
        queryGraphQL[key] = element;
      }
    }
  }

  const queryBuilt = jsonToGraphQLQuery(
    { query: queryGraphQL },
    { pretty: true },
  )

  return graph(queryBuilt)
}


export interface FindAllArgs {
  target: string
  alias?: string
  paranoid? : boolean
  offset?: number
  limit?: number
  output?: any
  order?: any
  where?: any
  values?: any
  search?: any
}
/**
 * @param target ressource name, User, Account, WorkSpace ...
 * @param alias mask name
 * @param attributes perforn data return. Scoping
 * @param limit add limit data (PS. put -1 for infinty data)
 * @param offset offset data
 * @param where query data
 * @param search query data
 * @param output output fields
 * @example
 * findAll({
 *    target: 'User',
 *    alias: 'users',
 *    where: value
 *    search: {'...'},
 *    offset: 0,
 *    limit: 10,
 *    order: [['name', 'ASC'], ['email', 'DESC']],
 *    output: {
 *      id: true,
 *      accountId: true,
 *      account: {
 *        id: true,
 *        name: true,
 *      },
 *    },
 *  })
 *  Return value ===> {
 *    id: ...
 *    accountId: ...
 *    account: {
 *      id: ...
 *      name: ...
 *    }
 * }
 */
export const findAll = async (args: FindAllArgs, ql = false) => {
  let query: string
  const a: any = {}
  let haveField: boolean = false
  for (const key of [
    'where',
    'values',
    'search',
    'offset',
    'limit',
    'attributes',
    'order',
  ]) {
    if (args[key]) {
      a[key] = args[key]
      haveField = true
    }
  }

  let io: any = {}
  if (haveField === true) {
    io.__args = a
  }

  if (args.alias) {
    io.__alias = args.alias
  }

  io = args.output ? { ...io, ...args.output } : { ...io, id: true }

  if (ql === true) {
    return {
      [`${uncapitalizeFirstLetter(pluralize.plural(args.target))}`]: io,
    }
  }

  query = jsonToGraphQLQuery(
    {
      query: {
        [`${uncapitalizeFirstLetter(pluralize.plural(args.target))}`]: io,
      },
    },
    { pretty: true },
  )
  return graph(query)
}

/**
 * @param alias alias function
 * @param target ressource name, User, Account, WorkSpace ...
 * @param id id of resources
 * @example
 * one({
 *    target: 'user',
 *    alias: 'user',
 *    id: 'XZx3_1Ap',
 *    output: {
 *      id: true,
 *      accountId: true,
 *      account: {
 *        id: true,
 *        name: true,
 *      },
 *    },
 *  })
 *  Return value ===> {
 *    id: ...
 *    accountId: ...
 *    account: {
 *      id: ...
 *      name: ...
 *    }
 * }
 */
interface IFindOne {
  target: string
  alias?: string
  id?: string
  where?: any
  output?: Output | boolean
}
export const findOne = async ({
  alias,
  target,
  id,
  where,
  output,
}: IFindOne, ql = false) => {
  let query: string

  // Secure
  if (output === undefined) {
    output = { id: true };
  }

  let args
  if (id) {
    args = { where: { id } }
  }

  if (where) {
    args = { where }
  }

  let values: any = { }
  if (typeof output === 'object') {
    values = { ...output };
  }

  if (alias) {
    values.__alias = alias
  }

  if (args) {
    values.__args = args
  }

  if (ql === true) {
    return {
      [`${uncapitalizeFirstLetter(target)}`]: values,
    }
  }

  query = jsonToGraphQLQuery(
    {
      query: {
        [`${uncapitalizeFirstLetter(target)}`]: values,
      },
    },
    { pretty: true },
  )
  return graph(query)
}

/**
 * @param alias alias function
 * @param target ressource name, User, Account, WorkSpace ...
 * @param where query of resources
 * @example
 * count({
 *    target: 'User',
 *    alias: 'user',
 *    where: {
 *      accountId: 'XZx3_1Ap'
 *    }
 *  })
 *  Return value ===> true
 * }
 */
export const count = async ({
  alias,
  target,
  where,
  search,
}: {
  alias?: any
  target: string
  where?: any
  search?: string
}, ql = false) => {
  let query: string
  const q: any = { __alias: alias }
  const args: any = {}

  if (where) {
    args.where = where
    q.__args = args
  }

  if (search) {
    args.search = search
    q.__args = args
  }

  if (alias) {
    q.__alias = alias
  }

  if (ql === true) {
    return { [`count${target}`]: q }
  }

  query = jsonToGraphQLQuery(
    {
      query: { [`count${target}`]: q },
    },
    { pretty: true },
  )
  return graph(query)
}

/**
 * @param alias alias function
 * @param target ressource name, User, Account, WorkSpace ...
 * @param values values
 * @param output output in ouput
 * @example
 * create({
 *    target: 'User',
 *    alias: 'user',
 *    values: {
 *      firstName: "Gérard",
 *      lastName: "TOKO",
 *      email: "gerard@youngapp.co",
 *      accountId: 'XZx3_1Ap'
 *    },
 *    output: {
 *      id: true,
 *      accountId: true,
 *      account: {
 *        id: true,
 *        name: true,
 *      },
 *    },
 *  })
 *  Return value ===> {
 *    id: ...
 *    accountId: ...
 *    account: {
 *      id: ...
 *      name: ...
 *    }
 * }
 */
interface ICreate {
  target: string
  alias?: string
  values: object
  output?: Output | boolean
}

export const create = async ({ alias, target, values, output }: ICreate) => {
  let query: string

  // Secure
  if (!output) {
    output = { id: true }
  }

  if (output === true) {
    output = {}
  }

  const io: any = { __alias: alias, ...output }
  if (values) {
    io.__args = { values }
  }

  query = jsonToGraphQLQuery(
    {
      mutation: { [`create${target}`]: io },
    },
    { pretty: true },
  )

  return graph(query)
}

/**
 * @param target ressource name, User, Account, WorkSpace ...
 * @param values values
 * @param id id of resources
 * @param output output in ouput
 * @example
 * update({
 *    target: 'User',
 *    alias: 'user',
 *    id: 'XZx3_1Ap'
 *    values: {
 *      firstName: "Borui",
 *      lastName: "Lui",
 *      email: "borui@youngapp.co",
 *    },
 *    output: {
 *      id: true,
 *      accountId: true,
 *      account: {
 *        id: true,
 *        name: true,
 *      },
 *    },
 *  })
 * Return value ===> {
 *    id: ...
 *    accountId: ...
 *    account: {
 *      id: ...
 *      name: ...
 *    }
 * }
 */
interface IUpdate {
  target: string
  alias?: string
  id: string
  values: object
  output?: Output
}
export const update = ({ alias, target, values, output, id }: IUpdate) => {
  let query: string

  // Secure
  if (!output) {
    output = { id: true }
  }

  query = jsonToGraphQLQuery(
    {
      mutation: {
        [`update${target}`]: {
          __alias: alias,
          __args: { id, values },
          ...output,
        },
      },
    },
    { pretty: true },
  )

  return graph(query)
}

/**
 * @param target ressource name, User, Account, WorkSpace ...
 * @param values values containing id
 * @param output output in ouput
 * @example
 * updateAll({
 *    target: 'User',
 *    alias: 'user',
 *    values: {
 *      id: 'XZx3_1Ap',
 *      firstName: "Borui",
 *      lastName: "Lui",
 *      email: "borui@youngapp.co",
 *    },
 *    output: {
 *      id: true,
 *      accountId: true,
 *      account: {
 *        id: true,
 *        name: true,
 *      },
 *    },
 *  })
 * Return value ===> {
 *    id: ...
 *    accountId: ...
 *    account: {
 *      id: ...
 *      name: ...
 *    }
 * }
 */
interface IUpdateAllArgs {
  target: string
  alias?: string
  output?: Output
  values: Array<{
    id: string
    [key: string]: any
  }>
}

export const updateAll = ({
  alias,
  target,
  values,
  output = { id: true },
}: IUpdateAllArgs) => {
  if (values.length === 0) {
    return
  }

  const query = values.map(({ id, ...value }, index) =>
    jsonToGraphQLQuery({
      [`update${target}`]: {
        __alias: `${alias}${index}`,
        __args: { id, values: value },
        ...output,
      },
    }),
  )

  return graph(`
    mutation {
      ${query}
    }`)
}

/**
 * @param target ressource name, User, Account, WorkSpace ...
 * @param values values initialize
 * @param id id of resources
 * @param output output in ouput
 * @example
 * duplicate({
 *    target: 'User',
 *    alias: 'user',
 *    id: 'XZx3_1Ap'
 *    values: {
 *      firstName: "Borui",
 *      lastName: "Lui",
 *      email: "borui@youngapp.co",
 *    },
 *    output: {
 *      id: true,
 *      accountId: true,
 *      account: {
 *        id: true,
 *        name: true,
 *      },
 *    },
 *  })
 * Return value ===> {
 *    id: ...
 *    accountId: ...
 *    account: {
 *      id: ...
 *      name: ...
 *    }
 * }
 */
interface IDuplicate {
  target: string
  alias?: string
  id: string
  values: object
  output?: Output
}
export const duplicate = async ({
  alias,
  target,
  values,
  output,
  id,
}: IDuplicate) => {
  let query: string

  // Secure
  if (!output) {
    output = { id: true }
  }

  query = jsonToGraphQLQuery(
    {
      mutation: {
        [`duplicate${target}`]: {
          __alias: alias,
          __args: { id, values },
          ...output,
        },
      },
    },
    { pretty: true },
  )
  return graph(query)
}

/**
 * @param target ressource name, User, Account, WorkSpace ...
 * @param alias mask name
 * @param id id of resources
 * @example
 * remove({
 *    target: 'User',
 *    alias: 'user',
 *    id: 'XZx3_1Ap'
 *  })
 * Return value ===> true
 */
interface IRemove {
  target: string
  alias?: string
  id?: string
  ids?: string[]
}

export const remove = ({ alias, target, id, ids }: IRemove) => {
  let query: string
  const args: any = {}
  if (ids) {
    args.ids = ids
  }
  if (id) {
    args.id = id
  }


  query = jsonToGraphQLQuery(
    {
      mutation: { [`delete${target}`]: { __alias: alias, __args: args } },
    },
    { pretty: true },
  )
  return graph(query)
}

/**
 * @param target ressource name, User, Account, WorkSpace ...
 * @param alias mask name
 * @param ids ids of resources
 * @example
 * removeAll({
 *    target: 'User',
 *    alias: 'user',
 *    ids: ['XZx3_1Ap', 'RNemR5EEU']
 *  })
 * Return value ===> true
 */
interface IRemoveAllArgs {
  target: string
  alias?: string
  ids: string[]
}

export const removeAll = ({ alias, target, ids }: IRemoveAllArgs) => {
  if (ids.length === 0) {
    return
  }

  const query = ids.map((id, index) =>
    jsonToGraphQLQuery({
      [`delete${target}`]: {
        __alias: `${alias}${index}`,
        __args: { id },
      },
    }),
  )

  return graph(`
    mutation {
      ${query}
    }`)
}

/**
 * @param email ressource...
 * @example
 * newAccountEmail({
 *    firstName: '...',
 *    lastName: '...',
 *    email: '...',
 *    name: '...'
 *  })
 * Return value ===> true
 * }
 */
export const newAccountEmail = async (args: any) => {
  const query = jsonToGraphQLQuery({
    query: {
      newAccountEmail: {
        __args: args,
      },
    },
  }, { pretty: true });
  return graph(query)
}

/**
 * @param target ressource name, User, Account, WorkSpace ...
 * @param values values
 * @param id id of resources
 * @param output output in ouput
 * @example
 * newAccount({
 *    firstName: '...',
 *    lastName: '...',
 *    email: '...',
 *    name: '...'
 *  })
 * Return value ===> true
 * }
 */
export const newAccount = async (args: any) => {
  const query = jsonToGraphQLQuery({
    query: {
      newAccount: {
        __args: args,
      },
    },
  }, { pretty: true });
  return graph(query)
}

export const newAccountFromMember = async (args: any) => {
  const query = jsonToGraphQLQuery({
    query: {
      newAccountFromMember: {
        __args: args,
        id: true,
        email: true,
        firstName: true,
        language: true,
        lastName: true,
        type: true
      },
    },
  }, { pretty: true });
  return graph(query)
}

/**
 * newPasswordFromAdmin
 * @param password
 */
export const newPasswordFromAdmin = async (password: string, oldPassword: string) => {
  let query: string;
  query = jsonToGraphQLQuery({
    query: {
      newPasswordFromAdmin: {
        __args: { password, oldPassword },
      },
    },
  }, { pretty: true });
  return graph(query)
};

/**
 * @param param0 module field
 * @example
 * login("email@test.fr", "test")
 * Return value ===> {
 *    id: ...
 *    accountId: ...
 *    account: {
 *      id: ...
 *      name: ...
 *    }
 * }
 */
export const accountFromSocial = async (params: any) => {
  let query: string;
  query = jsonToGraphQLQuery({
    query: {
      accountFromSocial: {
        __args: { ...params },
        user: {
          id: true,
          email: true,
          firstName: true,
          admin: true,
          mustChangePassword: true,
          lastName: true,
        },
        account: { admin: true, id: true, name: true, accountVerified: true },
        token: true,
      },
    },
  }, { pretty: true });
  return graph(query)
};


/**
 * @param target ressource name, User, Account, WorkSpace ...
 * @param values values
 * @param id id of resources
 * @param output output in ouput
 * @example
 * accountExist({
 *    email: '...',
 *    name: '...'
 *  })
 * Return value ===> true
 * }
 */
export const accountExist = async (args: any) => {
  const query = jsonToGraphQLQuery({
    query: {
      accountExist: {
        __args: args,
      },
    },
  }, { pretty: true });
  return graph(query)
}

/**
 * @param email email...
 * @param password value...
 * @example
 * login("email@test.fr", "test")
 * Return value ===> {
 *    id: ...
 *    accountId: ...
 *    account: {
 *      id: ...
 *      name: ...
 *    }
 * }
 */
export const login = async (email: string, password: string) => {
  let query: string
  query = jsonToGraphQLQuery({
    query: {
      login: {
        __args: { email, password },
        user: {
          id: true,
          email: true,
          firstName: true,
          language: true,
          mustChangePassword: true,
          admin: true,
          lastName: true,
        },
        account: { admin: true, id: true, name: true },
        token: true,
      },
    },
  })
  return graph(query)
}
/**
 * @param param0 module field
 * @example
 * newPassword("test", "TOKEN")
 * Return value ===> {
 *    id: ...
 *    accountId: ...
 *    account: {
 *      id: ...
 *      name: ...
 *    }
 * }
 */
export const newPassword = async (password: string, token: string) => {
  let query: string
  query = jsonToGraphQLQuery(
    {
      query: {
        newPassword: {
          __args: { password, token },
          user: {
            id: true,
            email: true,
            firstName: true,
            language: true,
            mustChangePassword: true,
            lastName: true,
          },
          account: { id: true, admin: true, name: true },
          token: true,
        },
      },
    },
    { pretty: true },
  )
  return graph(query)
}

export const generatePassword = async (
  mustChangePassword: boolean,
  id: string,
) => {
  let query: string
  query = jsonToGraphQLQuery(
    {
      query: {
        generatePassword: {
          __args: { mustChangePassword, id },
          __alias: 'password',
        },
      },
    },
    { pretty: true },
  )
  return graph(query)
}

export const transferData = async (from: string, to: string) => {
  let query: string
  query = jsonToGraphQLQuery(
    {
      query: {
        transferData: {
          __args: { from, to },
          __alias: 'transfered',
        },
      },
    },
    { pretty: true },
  )
  return graph(query)
}

/**
 * @param param0 module field
 * @example
 * sendEmailVerificationForNewPassword("email@test.fr")
 * @return {string} - Token
 */
export const sendEmailVerificationForNewPassword = async (
  email: string,
  language: string,
  url?: string,
) => {
  let query: string
  query = jsonToGraphQLQuery(
    {
      query: {
        sendEmailVerificationForNewPassword: {
          __args: { email, language, url },
          __alias: 'token',
        },
      },
    },
    { pretty: true },
  )
  return graph(query)
}

/**
 * @param token ressource name, User, Account, WorkSpace ...
 * @example
 * logout("XZx3_1Ap")
 * Return value ===> true
 */
export const logout = async (token: string) => {
  let query: string
  query = jsonToGraphQLQuery(
    {
      query: { logout: { __args: { token } } },
    },
    { pretty: true },
  )
  return graph(query)
}

/**
 * Has token
 * @param token token
 */
export const getToken = async (token: string) => {
  let query: string
  query = jsonToGraphQLQuery(
    {
      query: { getToken: { __args: { token }, owner: true } },
    },
    { pretty: true },
  )
  return graph(query)
}

/**
 * Has token
 * @param token token
 */
export const validateAccount = async (token: string) => {
  let query: string
  query = jsonToGraphQLQuery(
    {
      query: { validateAccount: { __args: { token } } },
    },
    { pretty: true },
  )
  return graph(query)
}



// WEBPACK FOOTER //
// ./src/utils/graph/GraphExplorer.ts